import { useEffect, useState } from 'react';
import { meApi } from '../api/meApi';
import { DataEditingLandingCard } from '../components/data-editing/DataEditingLandingCard';
import { EmptyState } from '../components/data-editing/EmptyState';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useAuth } from '../hooks/useAuth';
import type { FeatureCatalogEntry } from '../types/auth';

// Feature codes that are NOT standalone "Data Editing" pages and so must never get a card on
// this landing page, even though they live in the same feature catalog. `third_level_bid_editing`
// is an INLINE editor mode on the "Decision to make a 3rd level bid" practice page — it has no
// dedicated data-editing route — so it is excluded here (the grant still gates the inline mode).
const NON_LANDING_FEATURE_CODES = new Set<string>(['third_level_bid_editing']);

// Feature 025 — "Ask Jeff — Review" is role-gated, not grant-gated: it has no feature-catalog row
// because a catalog grant is something an Admin hands out, and this surface is closed to Admin.
// Its card is therefore appended here for the Super Editor rather than arriving from /me/features.
// The code doubles as the route via the same underscore→hyphen convention the cards use.
const ASK_JEFF_CARD: FeatureCatalogEntry = {
  code: 'ask_jeff_review',
  // Hyphen, not an em dash: matches this page's own <h1> and usePageTitle, and the
  // "3rd level bids - Review" convention the review-surface family already uses.
  displayName: 'Ask Jeff - Review',
  description: "The hands you flagged for Jeff, with your notes and the box for his comment.",
};

export function DataEditingLandingPage() {
  const { user } = useAuth();
  const [features, setFeatures] = useState<FeatureCatalogEntry[] | null>(null);

  const isSuperEditor = user?.role === 'Super Editor';

  useEffect(() => {
    meApi
      .getAccessibleFeatures()
      .then(all => setFeatures(all.filter(f => !NON_LANDING_FEATURE_CODES.has(f.code))))
      .catch(() => setFeatures([]));
  }, []);

  if (!features) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const cards = isSuperEditor ? [...features, ASK_JEFF_CARD] : features;

  if (cards.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-ink mb-6">Data Editing</h1>
        <EmptyState />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-ink mb-6">Data Editing</h1>
      <div className="grid gap-4 sm:grid-cols-2">
        {cards.map(feature => (
          <DataEditingLandingCard key={feature.code} feature={feature} />
        ))}
      </div>
    </div>
  );
}
